import { Injectable } from '@angular/core';

export abstract class AppCrypto {
  abstract encrypt(text: string): string;

  abstract decrypt(text: string): string;
}

/**
 * Use Base64 for encryption/decryption.
 */
@Injectable()
export class Base64AppCrypto extends AppCrypto {
  decrypt(text: string): string {
    if (text) {
      return atob(text);
    }
    return text;
  }

  encrypt(text: string): string {
    if (text) {
      return btoa(text);
    }
    return text;
  }
}

const UTF8_CRYPTO_PASS = 'UzBtM1NlY3IkdA==';

/**
 * Using XOR and conversion to Base64 strings.
 * Conversion to Base64 is done using solution #3 from Mozilla web site:
 *     https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding
 */
@Injectable()
export class Utf8AppCrypto extends AppCrypto {
  decrypt(text: string): string {
    if (text) {
      const data = this.atobUTF16(text);
      return this.xorStrings(data, UTF8_CRYPTO_PASS);
    }
    return text;
  }

  encrypt(text: string): string {
    if (text) {
      const data = this.xorStrings(text, UTF8_CRYPTO_PASS);
      return this.btoaUTF16(data);
    }
    return text;
  }

  /**
   * Using solution from Mozilla:
   * https://developer.mozilla.org/en-US/docs/Web/API/WindowBase64/Base64_encoding_and_decoding#Solution_3_%E2%80%93_JavaScript's_UTF-16_%3E_binary_string_%3E_base64
   */
  private btoaUTF16(sString: string): string {
    const aUTF16CodeUnits = new Uint16Array(sString.length);
    Array.prototype.forEach.call(aUTF16CodeUnits, (el, idx, arr) => {
      arr[idx] = sString.charCodeAt(idx);
    });
    return btoa(
      new Uint8Array(aUTF16CodeUnits.buffer).reduce((data, byte) => {
        return data + String.fromCharCode(byte);
      }, ''),
    );
  }

  private atobUTF16(sBase64: string): string {
    const sBinaryString = atob(sBase64);
    const aBinaryView = new Uint8Array(sBinaryString.length);
    Array.prototype.forEach.call(aBinaryView, (el, idx, arr) => {
      arr[idx] = sBinaryString.charCodeAt(idx);
    });
    return this.arrayBufferToString(aBinaryView.buffer);
  }

  arrayBufferToString(buffer: any): string {
    const bufView = new Uint16Array(buffer);
    const length = bufView.length;
    let result = '';
    let addition: number = Math.pow(2, 16) - 1;

    for (let i = 0; i < length; i += addition) {
      if (i + addition > length) {
        addition = length - i;
      }
      result += String.fromCharCode.apply(null, Array.prototype.slice.call(new Uint16Array(buffer)));
    }
    return result;
  }

  private xorStrings(input: string, key: string): string {
    let output = '';
    for (let i = 0; i < input.length; i++) {
      const c = input.charCodeAt(i);
      const k = key.charCodeAt(i % key.length);
      // tslint:disable-next-line:no-bitwise
      output += String.fromCharCode(c ^ k);
    }
    return output;
  }
}

/**
 * Do not encrypt.
 */
@Injectable()
export class NoAppEncryption extends AppCrypto {
  decrypt(text: string): string {
    return text;
  }

  encrypt(text: string): string {
    return text;
  }
}
